import { Flex } from '@enechain/ecloud-designsystem'

/**
 * スポット市場画面の **タブより上のステータス帯** (design `bidding/spot.html` の
 * `#spot-status-hub`)。
 *
 * 取得失敗 / 他受渡日の承認待ち / 取引可能エリア 0 件 / ブロック一覧取得失敗 /
 * 締切超過 の 5 本を 1 つの器にまとめる。 素の `Alert` を親の `gap={3}` (12px) で
 * 積むと、 複数出た時に画面上部だけで縦を使い切り、 下の入札一覧までスクロール
 * しないと届かない (issue #6671)。
 *
 * design と同じ圧縮をかける (`spot.html` の `#spot-status-hub` 節):
 * - 器: `gap: var(--space-2)` = 8px (親の 12px より詰める)
 * - alert: `padding: var(--space-2) var(--space-3)` = 8px 12px
 *
 * design はさらに title を `--font-size-md` + `margin-bottom: 0` にしているが、
 * **React 側では不要**。 DS `Alert` の title recipe は既に `textStyle: textMd`
 * (= 14px) で margin も持たない (実測: 注入 CSS に `font-size:14` のみ)。 当てても
 * no-op なので書かない。
 *
 * **description は圧縮しない。** design が `--font-size-xs` (10px) を当てているのは
 * `#spot-appr-bar` (= 承認ステータスバー) だけで、 そちらは `NormalBidTab` の中に
 * あり本器には入らない。 ここで全 alert に効かせると、 対象外の本文まで 10px に
 * 落ちる。
 *
 * **空の時は呼び出し元が本器ごと描画しない。** design は
 * `#spot-status-hub:not(:has(.ecl-alert:not([hidden]))) { display: none }` で消して
 * いるが、 React では中身が 1 つも無くても器が親 flex の item として残り、
 * 親の `gap` を 1 つぶん消費してしまう。 判定は呼び出し元が持つ
 * ([[crossDatePendingDates]] 等)。
 */
export const SpotStatusHub = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  <Flex
    // 器そのものには role が無く、 圧縮 style が届いているかを読むための入口が
    // 他に無いため testid を置く。
    flexDirection="column"
    // 高さ拘束された column flex の中なので、 既定の flex-shrink だと潰れる。
    // DS `Alert` は `overflow: hidden` を持つため潰れた分は切り落とされ、
    // 本文やボタンが欠ける (2026-08-19 デザイナー指摘と同じ罠)。
    flexShrink={0}
    gap={2}
    // [DS差異] chakra の slot class を選択して DS `Alert` の padding を上書きする。
    //
    // 本来は各 `Alert` に padding を prop で渡したいが、 5 本のうち 2 本
    // (`CrossDatePendingApprovalBanner` / `TradableAreasBanner`) は **中で `Alert` を
    // 組み立てる component** で、 外から padding を渡す口が無い。 5 箇所へ値を配ると
    // 圧縮値が散って drift するので、 器側の 1 箇所に寄せている。
    //
    // class 名は DS/chakra の内部なので、 更新で変わると圧縮が**黙って外れる**
    // (余白だけの退行なので unit test では固定しない。 DS 更新時に目視で確認する)。
    css={{ '& .chakra-alert__root': { padding: '8px 12px' } }}
  >
    {children}
  </Flex>
)
